import type { UserProfile } from '@/features/user';
import type { BracketMatch, BracketSlot, MatchStatus } from './bracketTypes';
import type { ApiMatch, ApiMatchStatus } from './types';

/**
 * Mapowanie danych API na modele widokowe dashboardu (karty meczow, plan dnia).
 * Czyste funkcje — bez Reacta, latwe do testowania.
 */

/** Skraca imie do inicjalu: „Michał Kowalski” -> „M. Kowalski”. */
export function shortName(firstName: string, lastName: string): string {
  const initial = firstName.trim().charAt(0);
  return initial ? `${initial}. ${lastName}`.trim() : lastName;
}

/** Buduje mape `userId -> krotka nazwa` z profili graczy. */
export function buildNameMap(users: readonly UserProfile[]): Map<string, string> {
  return new Map(users.map((user) => [user.id, shortName(user.firstName, user.lastName)]));
}

/** Sklada etykiete strony meczu — singiel albo para „A. X / B. Y”. */
function sideLabel(ids: readonly string[], names: ReadonlyMap<string, string>): string | null {
  if (ids.length === 0) return null;
  return ids.map((id) => names.get(id) ?? '…').join(' / ');
}

function toSlot(ids: readonly string[], names: ReadonlyMap<string, string>): BracketSlot {
  const label = sideLabel(ids, names);
  if (!label) return { kind: 'pending' };
  return { kind: 'player', player: { id: ids[0] ?? label, name: label } };
}

/** Status API -> status widokowy karty meczu (zawieszony pokazujemy jak zaplanowany). */
export function toViewStatus(status: ApiMatchStatus): MatchStatus {
  switch (status) {
    case 'COMPLETED':
      return 'finished';
    case 'IN_PROGRESS':
      return 'live';
    case 'SUSPENDED':
      return 'suspended';
    case 'WALKOVER':
      return 'walkover';
    default:
      return 'scheduled';
  }
}

/** Skraca „14:30:00” z API do „14:30”. */
export function shortTime(time: string | null): string | null {
  if (!time) return null;
  return time.slice(0, 5);
}

/** Mapuje mecz z API na model karty meczu (LiveMatchCard / MatchScoreRow). */
export function toBracketMatch(match: ApiMatch, names: ReadonlyMap<string, string>): BracketMatch {
  const homeScores = match.setScores.map((set) => set.score1);
  const awayScores = match.setScores.map((set) => set.score2);

  return {
    id: match.id,
    home: toSlot(match.team1Ids, names),
    away: toSlot(match.team2Ids, names),
    ...(homeScores.length > 0 ? { homeScores, awayScores } : {}),
    ...(match.result === 'PLAYER1_WIN'
      ? { winner: 'home' as const }
      : match.result === 'PLAYER2_WIN'
        ? { winner: 'away' as const }
        : {}),
    status: toViewStatus(match.status),
    ...(shortTime(match.scheduledTime) ? { scheduleLabel: shortTime(match.scheduledTime)! } : {}),
  };
}

/** Zbiera identyfikatory wszystkich graczy z listy meczow (do batcha nazwisk). */
export function collectPlayerIds(matches: readonly ApiMatch[]): string[] {
  const ids = new Set<string>();
  for (const match of matches) {
    for (const id of match.team1Ids) ids.add(id);
    for (const id of match.team2Ids) ids.add(id);
  }
  return [...ids];
}

/** Procentowa zmiana miedzy oknami (np. mecze zakonczone 7 dni vs poprzednie 7). */
export function trendPercent(current: number, previous: number): number | null {
  if (previous <= 0) return null;
  return Math.round(((current - previous) / previous) * 100);
}

/** Tlumaczy kod rundy z API („F”, „SF”, „R1”, „Round 2”) na etykiete PL. */
/**
 * Kolor akcentu dla fazy turnieju — sam tekst, bez tla.
 *
 * Im blizej rozstrzygniecia, tym cieplejszy odcien: cwiercfinal chlodny,
 * final koralowy. Dzieki temu na dlugiej liscie meczow widac uklad fazy
 * bez czytania etykiet. Faza grupowa i kolejki zostaja bez akcentu —
 * jest ich duzo i kolor przestalby cokolwiek znaczyc.
 *
 * Klasy sa pelnymi literalami, bo Tailwind skanuje kod statycznie.
 */
export function roundAccent(round: string | null): string | null {
  if (!round) return null;
  switch (round) {
    case 'F':
      return 'text-accent-coral';
    case '3P':
      return 'text-accent-orange';
    case 'SF':
      return 'text-accent-purple';
    case 'QF':
      return 'text-accent-blue';
    default:
      return /^R\d+$/.test(round) ? 'text-accent-teal' : null;
  }
}

export function roundLabel(round: string | null): string | null {
  if (!round) return null;
  switch (round) {
    case 'F':
      return 'Finał';
    case 'SF':
      return 'Półfinał';
    case 'QF':
      return 'Ćwierćfinał';
    case '3P':
      return 'Mecz o 3. miejsce';
    default: {
      const knockout = /^R(\d+)$/.exec(round);
      if (knockout) return `${knockout[1]}. runda`;
      const roundRobin = /^Round (\d+)$/.exec(round);
      if (roundRobin) return `${roundRobin[1]}. kolejka`;
      return round;
    }
  }
}
